import { useEffect, useRef, useState } from 'react';
import { constants } from '../../common/constants/constants';

// How often an open report re-checks a job whose dealer report is still being processed
export const PROCESSING_POLL_INTERVAL_MS = 60 * 1000;

// The watermarked dealer PDF of a job, opened by plain URL (the file route needs no token)
export const reportFileUrl = job => {
    const file = ((job && job.files) || []).find(f => f.fileName && f.fileName.includes('report'));
    return file ? `${constants.API_BASE_URL}/jobs/files/file/${file.fileName}` : '';
};

// Where a report page loads its job from: the sample job, a shared link, or the job uuid; null when none applies.
// `lang` is the page's language, so a report past the translation cut-off comes back in it (SC-QA-119)
export const jobLoader = ({ sample, sharedLink, jobUuid, getReport, getReportBySharedLink, lang }) => {
    if (sample) return () => Promise.resolve(sample);
    if (sharedLink) return () => getReportBySharedLink(sharedLink, lang);
    if (jobUuid) return () => getReport(jobUuid, lang);
    return null;
};

/**
 * Loads the dealer job of a report and, while its report is being processed, checks it again every minute so an
 * open page switches to the report without a reload. `load` is the request (by uuid or shared link); a falsy
 * `load` (no job to load) gives { job: null, loading: false }. A change of `refresh` (Try again on a translation
 * notice) asks again quietly: the page keeps its job, and the reader's place, until the new one arrives.
 */
export const useDealerJob = (load, deps, refresh = 0) => {
    const [state, setState] = useState({ job: null, loading: !!load, error: false });
    const loadRef = useRef(load);
    loadRef.current = load;

    useEffect(() => {
        let cancelled = false;
        if (!load) {
            setState({ job: null, loading: false, error: false });
            return undefined;
        }

        setState(current => ({ ...current, loading: true }));
        load()
            .then(job => !cancelled && setState({ job, loading: false, error: false }))
            .catch(error => !cancelled && setState({ job: null, loading: false, error: error || true }));

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        if (!refresh || !loadRef.current) return undefined;
        let cancelled = false;
        loadRef
            .current()
            .then(job => !cancelled && job && setState(current => ({ ...current, job })))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [refresh]);

    const processing = !!state.job && state.job.reportState === 'processing';
    useEffect(() => {
        if (!processing) return undefined;

        const timer = setInterval(() => {
            loadRef
                .current()
                .then(job => setState(current => ({ ...current, job })))
                .catch(() => undefined);
        }, PROCESSING_POLL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [processing]);

    return state;
};
